/**
 * Spreadsheet round-trip for the duty roster.
 *
 * The sheet is deliberately the shape a ward sister already keeps by hand:
 * one row per employee, one column per date, a short shift code in each cell.
 * That makes the export usable as a printed rota and the import forgiving of a
 * sheet somebody edited in Excel rather than one produced by this app.
 *
 * Parsing happens here, in the browser, because SheetJS is already a dependency
 * and because the parsed result goes back through the ordinary cell-delta save.
 * Validation is NOT here — the same PUT that writes runs every check with
 * `validate_only` first, so the preview the user approves is produced by the
 * code that will do the writing rather than by a second implementation of the
 * rules that can drift from it.
 */
import * as XLSX from "xlsx";

// Cell tokens that mean something other than a shift.
export const WEEK_OFF_TOKENS = ["WO", "W/O", "OFF", "WEEKOFF", "WEEK OFF"];
export const LEAVE_TOKENS = ["L", "LV", "LEAVE"];
export const HOLIDAY_TOKENS = ["H", "HOL", "HOLIDAY"];
export const BLANK_TOKENS = ["", "-", "—", "NA", "N/A"];

const EMPLOYEE_CODE_HEADER = "Employee Code";
const EMPLOYEE_NAME_HEADER = "Employee Name";
const DEPARTMENT_HEADER = "Department";

const norm = (value) => String(value ?? "").trim().toUpperCase();

const formatClock12 = (value) => {
    if (!value) return "";
    const match = String(value).trim().match(/^(\d{1,2}):(\d{2})/);
    if (!match) return String(value);
    let hours = parseInt(match[1], 10);
    const minutes = match[2];
    const suffix = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;
    return `${hours}:${minutes} ${suffix}`;
};

const formatClockRange12 = (from, to, separator = "-") => {
    const start = formatClock12(from);
    const end = formatClock12(to);
    if (!start && !end) return "";
    if (start && end) return `${start}${separator}${end}`;
    return start || end;
};

/** The cell text for one resolved day, matching what the grid draws. */
export const cellToken = (cell, shiftsById) => {
    if (!cell) return "";
    if (cell.is_holiday) return "H";
    if (cell.is_weekoff) return "WO";
    if (cell.is_leave) return "L";
    if (!cell.shift_id) return "";
    return shiftsById[cell.shift_id]?.code || "";
};

/**
 * Build the worksheet matrix. Returned rather than written so the caller can
 * decide between download and preview, and so this stays testable.
 */
export const buildSheetMatrix = (rows, dates, shifts) => {
    const shiftsById = Object.fromEntries(shifts.map((s) => [s.id, s]));
    const header = [
        EMPLOYEE_CODE_HEADER,
        EMPLOYEE_NAME_HEADER,
        DEPARTMENT_HEADER,
        ...dates.map((d) => d.date),
    ];
    const body = rows.map((row) => {
        const byDate = Object.fromEntries((row.cells || []).map((c) => [c.date, c]));
        return [
            row.employee?.employee_id || "",
            row.employee?.employee_name || "",
            row.employee?.department?.name || "",
            ...dates.map((d) => cellToken(byDate[d.date], shiftsById)),
        ];
    });
    return [header, ...body];
};

/** A legend sheet, so whoever fills the file in knows the codes. */
const buildLegendMatrix = (shifts) => [
    ["Code", "Means"],
    ...shifts.map((s) => [
        s.code,
        `${s.name}${s.shift_timings_from ? ` (${formatClockRange12(s.shift_timings_from, s.shift_timings_to)})` : ""}`,
    ]),
    ["WO", "Week off"],
    ["L", "Leave"],
    ["H", "Holiday (read-only)"],
    ["(blank)", "No shift planned"],
];

export const downloadSheet = (rows, dates, shifts, fileName) => {
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
        workbook,
        XLSX.utils.aoa_to_sheet(buildSheetMatrix(rows, dates, shifts)),
        "Duty Roster",
    );
    XLSX.utils.book_append_sheet(
        workbook,
        XLSX.utils.aoa_to_sheet(buildLegendMatrix(shifts)),
        "Legend",
    );
    XLSX.writeFile(workbook, fileName);
};

/**
 * Turn an uploaded sheet into cell deltas.
 *
 * Returns `{ cells, problems }`. `problems` are things that can only be seen
 * here — an unreadable file, an employee code that is not on screen, a code
 * that matches no shift. Everything that needs the database (scope, locks,
 * dates) is left to the server's validate pass.
 *
 * Only cells that DIFFER from what is currently on screen are emitted, so
 * re-importing an unmodified export is correctly a no-op.
 */
export const parseSheet = async (file, { rows, dates, shifts }) => {
    const buffer = await file.arrayBuffer();
    const workbook = XLSX.read(buffer, { type: "array" });
    const sheetName = workbook.SheetNames.find((n) => norm(n) === norm("Duty Roster"))
        || workbook.SheetNames[0];
    const matrix = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], {
        header: 1, raw: false, defval: "",
    });

    if (!matrix.length) {
        return { cells: [], problems: [{ reason: "The sheet is empty" }] };
    }

    const header = matrix[0].map((h) => String(h ?? "").trim());
    const codeIndex = header.findIndex((h) => norm(h) === norm(EMPLOYEE_CODE_HEADER));
    if (codeIndex < 0) {
        return {
            cells: [],
            problems: [{ reason: `Could not find an "${EMPLOYEE_CODE_HEADER}" column` }],
        };
    }

    // Map each date column by its header text. Accept both the ISO form this
    // app exports and whatever Excel may have reformatted it into, by matching
    // against the dates we asked for.
    const dateByColumn = {};
    header.forEach((text, index) => {
        if (index === codeIndex) return;
        const cleaned = String(text ?? "").trim();
        const match = dates.find((d) => d.date === cleaned)
            || dates.find((d) => new Date(cleaned).toDateString() === new Date(d.date).toDateString());
        if (match) dateByColumn[index] = match.date;
    });
    if (!Object.keys(dateByColumn).length) {
        return { cells: [], problems: [{ reason: "No date columns in the sheet match the selected range" }] };
    }

    const employeeByCode = {};
    const currentByKey = {};
    rows.forEach((row) => {
        const code = norm(row.employee?.employee_id);
        if (code) employeeByCode[code] = row;
        (row.cells || []).forEach((cell) => {
            currentByKey[`${row.employee?.id}|${cell.date}`] = cell;
        });
    });

    // Shift lookup by code AND by name, so a hand-typed "Night" works as well
    // as "N".
    const shiftByToken = {};
    shifts.forEach((s) => {
        if (s.code) shiftByToken[norm(s.code)] = s;
        if (s.name) shiftByToken[norm(s.name)] = s;
    });

    const cells = [];
    const problems = [];

    for (let r = 1; r < matrix.length; r += 1) {
        const sheetRow = matrix[r];
        if (!sheetRow || !sheetRow.length) continue;
        const code = norm(sheetRow[codeIndex]);
        if (!code) continue;

        const row = employeeByCode[code];
        if (!row) {
            problems.push({
                row: r + 1,
                code: sheetRow[codeIndex],
                reason: "Not in the current view — check the department filter and page",
            });
            continue;
        }

        Object.entries(dateByColumn).forEach(([columnIndex, date]) => {
            const token = norm(sheetRow[columnIndex]);
            const current = currentByKey[`${row.employee.id}|${date}`];

            // Holiday cells are informational in the export; a planner cannot
            // create one here, so leaving or repeating "H" is not a change.
            if (HOLIDAY_TOKENS.includes(token)) return;
            if (current?.is_locked) return;

            let next;
            if (BLANK_TOKENS.includes(token)) {
                next = { shift: null, is_weekoff: false, is_leave: false };
            } else if (WEEK_OFF_TOKENS.includes(token)) {
                next = { shift: null, is_weekoff: true, is_leave: false };
            } else if (LEAVE_TOKENS.includes(token)) {
                next = { shift: null, is_weekoff: false, is_leave: true };
            } else {
                const shift = shiftByToken[token];
                if (!shift) {
                    problems.push({
                        row: r + 1,
                        code: sheetRow[codeIndex],
                        date,
                        reason: `"${sheetRow[columnIndex]}" is not a known shift code`,
                    });
                    return;
                }
                next = { shift: shift.id, is_weekoff: false, is_leave: false };
            }

            const unchanged = current
                && (current.shift_id || null) === next.shift
                && !!current.is_weekoff === next.is_weekoff
                && !!current.is_leave === next.is_leave;
            if (unchanged) return;

            cells.push({ employee: row.employee.id, date, ...next });
        });
    }

    return { cells, problems };
};
